import React, { useState, useEffect, useCallback } from 'react';
import Price from './Price';
import Checkbox from './Checkbox';
import {getFromHub, postToHub} from "../actions/networkActions";
import {updateCart} from "../actions/cartActions";
import ModalFooter from "./ModalFooter";
import {getHubBaseUrl} from "../utils/configUtils";
import { getExcursionText } from "../utils/themeUtils";
import { Trans, useLingui } from '@lingui/react/macro';
import { i18n } from '@lingui/core';
import { formatAddToPlannerPrice } from '../utils/cartUtils';
import { checkForOverlaps } from '../utils/overlapUtils';
import OverlapWarning from './OverlapWarning';

function AddToPlannerForm(props) {
    const [hasLoaded, setHasLoaded] = useState(false);
    const [passengers, setPassengers] = useState([]);
    const [bookingInformationId, setBookingInformationId] = useState("");
    const [initialSelected, setInitialSelected] = useState(0);
    const [nrAdultsBooked, setNrAdultsBooked] = useState(0);
    const [updateCartText, setUpdateCartText] = useState("");
    const [successText, setSuccessText] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [nrAvailable, setNrAvailable] = useState(0);
    const [waiverCheckboxChecked, setWaiverCheckboxChecked] = useState(false);
    const [waitlistActivated, setWaitlistActivated] = useState(false);
    const [currentWaitlistAvailability, setCurrentWaitlistAvailability] = useState(0);
    const [showWaitlistTexts, setShowWaitlistTexts] = useState(false);
    const [overlapElements, setOverlapElements] = useState([]);
    const [addingToCart, setAddingToCart] = useState(false);

    const {t} = useLingui();

    const excursionText = getExcursionText();
    const capitalisedExcursionText = getExcursionText(true);

    const statusMessages = {
        Bookable: "",
        AlreadyInCart: "",
        NoPriceAvailable: t({id: "booking_status_no_price"}),
        TimeOverlapWithOtherBooking: t({id: "booking_status_time_overlap_booking"}),
        AlreadyBooked: t({id: "booking_status_already_reserved"}),
        TimeOverlapWithOtherCartEntry: t({id: "booking_status_time_overlap_cart"}),
        FirstCruiseDay: t({id: "booking_status_first_day"}),
        LastCruiseDay: t({id: "booking_status_last_day"}),
        MinimumAge: t({id: "booking_status_below_min_age"}),
        MaximumAge: t({id: "booking_status_above_max_age"}),
        AlreadyWaitlisted: t({id: "booking_status_already_waitlisted"}),
    };

    useEffect(() => {
        getFromHub(
            `prebooking/cruises/itinerary/${props.itineraryId}/excursions/${props.excursionId}/bookingInformation`,
            updateBookingInformation,
        );
        // eslint-disable-next-line
    }, [props.itineraryId, props.excursionId]);

    const updateBookingInformation = (bookingInformation) => {
        let nrSelected = 0;
        let nrAdultsBooked = 0;

        const updatedPassengers = bookingInformation.passengers.map((passenger) => {
            passenger.selected = passenger.bookable && passenger.status === "AlreadyInCart";
            passenger.reason = statusMessages[passenger.status];
            passenger.isAdult = (passenger.ageType === "Adult");

            if(passenger.selected) {
                nrSelected++;
            }

            if(passenger.isAdult && passenger.status === "AlreadyBooked") {
                nrAdultsBooked++;
            }

            return passenger;
        });

        setPassengers(updatedPassengers);
        setNrAdultsBooked(nrAdultsBooked);
        setHasLoaded(true);
        setInitialSelected(nrSelected);
        setBookingInformationId(bookingInformation.bookingInformation);
        setNrAvailable(bookingInformation.numberOfAvailabilities);
        setWaitlistActivated(bookingInformation.cruiseExcursionBookingInformation.waitlistActivated);
        setCurrentWaitlistAvailability(bookingInformation.cruiseExcursionBookingInformation.currentWaitlistAvailability);
    };

    const getTotalPrice = useCallback(() => {
        return passengers.reduce((total, passenger) => {
            if (passenger.selected) {
                return total + passenger.price;
            }
            return total;
        }, 0);
    }, [passengers]);

    // Determine UI texts and error messages based on selected passengers
    const chooseTextsBasedOnSelectedPassengers = (nrSelected, nrAdultsSelected) => {
        let updateCartText = "";
        let successText = "";
        let errorMessage = "";
        let showWaitlistTexts = false;

        if(waitlistActivated) {
            if(nrSelected > currentWaitlistAvailability) {
                errorMessage = i18n._({
                    id: "too_many_passengers_selected_waitlist_text",
                    message: `Too many passengers selected, this {excursionText} only has {count, plural, one {# space} other {# spaces}} left on the waitlist`,
                    values: { count: currentWaitlistAvailability, excursionText: excursionText }
                });
            }
        } else {
            if(nrSelected > nrAvailable) {
                if(nrSelected > currentWaitlistAvailability) {
                    const moreAvailableThanWaitlist  = nrAvailable >= currentWaitlistAvailability;
                    if(moreAvailableThanWaitlist) {
                        errorMessage = i18n._({
                            id: "too_many_passengers_selected_text",
                            message: `Too many passengers selected, this {excursionText} only has {count, plural, one {# space} other {# spaces}} left`,
                            values: { count: nrAvailable, excursionText: excursionText }
                        });
                    } else {
                        errorMessage = i18n._({
                            id: "too_many_passengers_selected_waitlist_text",
                            message: `Too many passengers selected, this {excursionText} only has {count, plural, one {# space} other {# spaces}} left on the waitlist`,
                            values: { count: currentWaitlistAvailability, excursionText: excursionText }
                        });
                    }
                } else {
                    // there are enough spaces left on waitlist, use that and let them proceed with booking
                    showWaitlistTexts = true;
                }
            }
        }

        // If there has been an error message until this point, just return it and don't do other texts
        if(errorMessage) {
            return {
                updateCartText,
                successText,
                errorMessage,
                showWaitlistTexts,
            };
        }

        // Only children selected and no adult already booked
        if(nrAdultsBooked === 0 && nrSelected > 0 && nrAdultsSelected === 0) {
            errorMessage = t({id: "adults_must_accompany_children"});
        } else {
            // At least one adult selected or no passengers selected or at least one adult already booked
            if(initialSelected > 0) {
                if(nrSelected > 0) {
                    updateCartText = (waitlistActivated || showWaitlistTexts) ? t({id: "update_waitlist"}) : t({id: "update_planner"});
                    successText = (waitlistActivated || showWaitlistTexts) ?
                        t({id: "waitlist_reservation_is_updated"}) :
                        t({id: "updated_in_planner", message: `${capitalisedExcursionText} updated in your planner`});
                } else {
                    updateCartText = (waitlistActivated || showWaitlistTexts) ? t({id: "remove_from_waitlist"}) : t({id: "cancel_from_planner"});
                    successText = (waitlistActivated || showWaitlistTexts) ?
                        t({id: "waitlist_reservation_is_removed"}):
                        t({id: "removed_from_planner", message: `${capitalisedExcursionText} removed from your planner`});
                }
            } else {
                if(nrSelected > 0) {
                    updateCartText = (waitlistActivated || showWaitlistTexts) ? t({id: "add_to_waitlist"}) : t({id: "add_to_planner"});
                    successText = (waitlistActivated || showWaitlistTexts) ?
                        t({id: "waitlist_reservation_is_added"}):
                        t({id: "excursion_added_to_planner", message: `${capitalisedExcursionText} added to your planner`});
                }
            }
        }

        return {
            updateCartText,
            successText,
            errorMessage,
            showWaitlistTexts,
        };
    };

    const handleCheckboxChange = (id) => {
        let nrSelected = 0;
        let nrAdultsSelected = 0;

        const updatedPassengers = passengers.map((passenger) => {
            if (passenger.id === id) {
                passenger.selected = !passenger.selected;
            }
            if(passenger.selected) {
                nrSelected++;
                if(passenger.isAdult) {
                    nrAdultsSelected++;
                }
            }
            return passenger;
        });

        const { updateCartText, successText, errorMessage, showWaitlistTexts } = chooseTextsBasedOnSelectedPassengers(nrSelected, nrAdultsSelected);
        setPassengers(updatedPassengers);
        setUpdateCartText(updateCartText);
        setSuccessText(successText);
        setErrorMessage(errorMessage);
        setShowWaitlistTexts(showWaitlistTexts);
    };

    const postToCart = async () => {
        setAddingToCart(true);
        const data = passengers.map((passenger) => {
            return {
                bookingInformation: bookingInformationId,
                passenger: passenger.id,
                action: passenger.selected ? "add" : "remove",
                state: showWaitlistTexts || waitlistActivated ? "Waitlisted" : "Bookable",
            };
        });

        await postToHub('prebooking/cart', data);
        setAddingToCart(false);
        props.onSuccess(successText);
        await updateCart();
    };

    const addToCart = async () => {
        if (props.fromTime && props.toTime) {
            const overlaps = await checkForOverlaps(props.fromTime, props.toTime, {
                currentExcursionId: props.excursionId
            });
            if (overlaps.length) {
                setOverlapElements(overlaps);
                return;
            }
        }

        await postToCart();
    };

    const toggleWaiverCheckboxChecked = () => {
        setWaiverCheckboxChecked((prev) => !prev);
    };

    if (overlapElements.length > 0) {
        return (
            <OverlapWarning
                overlapElements={overlapElements}
                onCancel={() => setOverlapElements([])}
                onConfirm={postToCart}
                confirmDisabled={addingToCart}
            />
        );
    }

    if(!hasLoaded) {
        return (<div><span translate="no"><Trans id="loading">loading</Trans>...</span></div>);
    }

    return (
        <div className="flex flex-col grow">
            <div className="grow bg-white py-4 px-2 sm:px-4">
                <div className="font-bold">
                    {waitlistActivated || showWaitlistTexts ?
                        t({id: "who_is_joining_waitlist", message: `Who's joining the waitlist for the ${excursionText}`}) :
                        t({id: "who_is_travelling", message: `Who's travelling on the ${excursionText}`})
                    }
                </div>
                <div>
                    {formatAddToPlannerPrice(props.adultPrice, props.childPrice, props.minimumAge, props.currencySymbol)}
                </div>

                <div className="bg-primary-lightest px-4 py-2 mt-4">
                    {passengers.map((passenger) =>
                        <div className="my-2" key={passenger.id}>
                            <Checkbox
                                label={`${passenger.firstName} ${passenger.lastName}`}
                                checked={passenger.selected}
                                disabled={!passenger.bookable}
                                disabledReason={passenger.reason}
                                handleCheckboxChange={() => handleCheckboxChange(passenger.id)}
                            />
                        </div>
                    )}
                </div>

                <Price preText={t({id: "total_price"})} price={getTotalPrice()} currencySymbol={props.currencySymbol}/>
                { waitlistActivated &&
                <div className="mt-1 text-sm">
                    <Trans id="charge_will_be_added_to_onboard_account">
                        The charge will only be added to onboard account once a space on the {getExcursionText()} has been provided
                    </Trans>
                </div>
                }

                { errorMessage &&
                    <div className="errors mt-2">
                        {errorMessage}
                    </div>
                }

                {props.waiverPdfUrl &&
                <div className="mt-4">
                    <Checkbox
                        checked={waiverCheckboxChecked}
                        value="waiver"
                        handleCheckboxChange={toggleWaiverCheckboxChecked}
                    >
                        <Trans id="i_have_read_the_waiver">
                            I have read {" "}
                            <a href={`${getHubBaseUrl(false)}${props.waiverPdfUrl?.url}`}
                            className="underline text-primary"
                            target="_blank"
                            rel="noopener noreferrer">
                                the Waiver
                            </a>
                        </Trans>
                    </Checkbox>
                </div>
                }

                {
                    !waitlistActivated && showWaitlistTexts &&
                        <div className="mt-4 text-lg">
                            <Trans id="reservation_added_to_waitlist_will_be_notified">
                                Please note: your reservation is being added to a waitlist
                                and you will be notified once space on the {getExcursionText()} becomes available.
                            </Trans>
                        </div>
                }
            </div>

            <ModalFooter
                onCancel={props.onCancel}
                actionDisabled={(updateCartText === "") || (props.waiverPdfUrl && !waiverCheckboxChecked)}
                actionButtonText={updateCartText || (waitlistActivated ? t({id: "add_to_waitlist"}) : t({id: "add_to_planner"}))}
                onActionClick={addToCart}
            />
        </div>
    );
}

export default AddToPlannerForm;